import { create } from 'zustand';
import { isPopupMfe } from './popup-mfe-constants';

interface PopupMfeState {
  /** Active popup MFE appId (lowercased), or null when no popup is open. */
  appId: string | null;
  /** Optional sub-path within the popup MFE (e.g. "contact-support"). */
  subPath: string | null;
  open: (appId: string, subPath?: string) => void;
  close: () => void;
}

/**
 * Holds the single active popup MFE. Mirrors the legacy named `popup`
 * router-outlet (only one popup app is shown at a time). `PopupMfeOverlay`
 * subscribes to render the overlay; imperative helpers below drive it from
 * non-React callers (the legacy `EY_RAPToR_*` window globals).
 */
export const usePopupMfeStore = create<PopupMfeState>((set) => ({
  appId: null,
  subPath: null,
  open: (appId, subPath) => set({ appId: appId.toLowerCase(), subPath: subPath || null }),
  close: () => set({ appId: null, subPath: null }),
}));

/**
 * Timestamp of the last imperative open (Date.now()). Used by
 * `usePopupUrlSync` to distinguish a genuine URL-driven close (browser Back)
 * from a spurious one caused by a transient navigation stripping `?popup=`
 * before the Store→URL sync has propagated. Mirrors Angular's behaviour where
 * the popup route outlet is immune to search-param races.
 */
let lastImperativeOpenAt = 0;

/** Grace window (ms) after an imperative open during which the URL→Store sync
 *  will NOT close the popup. Allows the Store→URL `replaceState` to propagate
 *  through TanStack Router before the URL→Store effect can misread a stale URL. */
const OPEN_GRACE_MS = 2000;

/**
 * Imperative open — used by `EY_RAPToR_routeToPopUpMFE` and header buttons.
 * Ignores ids that aren't registered popup MFEs so a stray call can't mount an
 * arbitrary app in the overlay.
 */
export function openPopupMfe(appId: string, subPath?: string): boolean {
  if (!isPopupMfe(appId)) return false;
  lastImperativeOpenAt = Date.now();
  usePopupMfeStore.getState().open(appId, subPath);
  return true;
}

/**
 * Whether the popup was opened imperatively within the grace window.
 * URL→Store sync should NOT close the popup during this period.
 */
export function isWithinOpenGrace(): boolean {
  return Date.now() - lastImperativeOpenAt < OPEN_GRACE_MS;
}

/** Legacy window event fired whenever a popup MFE closes (Angular parity). */
const POPUP_MFE_CLOSED_EVENT = 'EY.RAPToR.PopUpMFE.Closed';

/** Imperative close — used by `EY_RAPToR_closeMFEModalPopup` and the overlay chrome. */
export function closePopupMfe(): void {
  if (import.meta.env.DEV) {
    console.trace('[shell] closePopupMfe called');
  }
  const closedAppId = usePopupMfeStore.getState().appId;
  usePopupMfeStore.getState().close();
  // Legacy parity: `ModalPopupComponent.ngOnDestroy` emits a window
  // `CustomEvent('EY.RAPToR.PopUpMFE.Closed', { detail: appId })` on EVERY popup
  // close, and MFEs subscribe via `EY_RAPToR_receiveEvent(...)`
  // (`window.addEventListener` + `event.detail`). Mirror that exact shape here so
  // any MFE relying on the close signal keeps working. Only fire when a popup was
  // actually open (the Angular component existed only then), so the unconditional
  // `closePopupMfe()` calls from the Apps/Teams/Help buttons don't emit a spurious
  // event.
  if (closedAppId && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(POPUP_MFE_CLOSED_EVENT, { detail: closedAppId }));
  }
  // Reconcile the client store with localStorage. The orchestrator (and other
  // popup MFEs) may call `resetToGlobalClient()` before closing, writing the
  // original client back to localStorage. Angular's dropdown reads localStorage
  // directly on re-render so it auto-syncs; React must explicitly push the
  // change into the Zustand store so the dropdown reflects the restored client.
}
